import {useEffect, useState} from 'react';

import {isNotNil} from 'ramda';

import {
  CustomerResponseBodyV2,
  useGetVehicleCustomerQuery,
  useLazyGetCustomerV2Query,
} from '@dms/api/customer';

import {getCustomerName} from '../../../utils/getCustomerName';
import {getOwnershipTitle} from '../utils/getOwnershipTitle';

interface VehicleCustomer extends CustomerResponseBodyV2 {
  fullName: string;
  ownershipTypeTranslated: string;
}

interface UseGetCustomersDetailsProps {
  vehicleId: string;
}

export function useGetCustomersDetails(props: UseGetCustomersDetailsProps) {
  const {data: vehicleCustomers} = useGetVehicleCustomerQuery({vehicleId: props.vehicleId});
  const [getCustomer] = useLazyGetCustomerV2Query();

  const [gdprCustomers, setGdprCustomers] = useState<VehicleCustomer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(
    function loadCustomersDetails() {
      const fetchCustomerDetails = async () => {
        if (!vehicleCustomers) {
          return;
        }

        setIsLoading(true);
        setIsError(false);

        try {
          const customers = await Promise.all(
            vehicleCustomers.map((customer) =>
              getCustomer({customerId: customer?.customerId ?? ''}, true).then(({data}) => data)
            )
          );

          const filteredCustomers = customers.filter(isNotNil).map((customer): VehicleCustomer => {
            const ownershipType = vehicleCustomers.find(
              (item) => item?.customerId === customer.id
            )?.customerType;

            return {
              ...customer,
              fullName: getCustomerName(customer) ?? '',
              ownershipTypeTranslated: ownershipType ? getOwnershipTitle(ownershipType) : '',
            };
          });

          setGdprCustomers(filteredCustomers);
        } catch {
          setIsError(true);
        } finally {
          setIsLoading(false);
        }
      };

      fetchCustomerDetails();
    },
    [vehicleCustomers, getCustomer]
  );

  return {
    gdprCustomers,
    isLoading,
    isError,
  };
}
